import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './activities.css';
import { useTranslation, Trans } from 'react-i18next';
import { BsArrowLeft } from 'react-icons/bs';
import { config } from "../../config";
import Footer from '../Footer/Footer';
import { useLocalized } from '../../i18n/useLocalized';

const MediaContent = ({ media, mediaType, alt }) => {
    if (mediaType === 'video') {
        return (
            <video
                muted
                autoPlay
                loop
                playsInline
                preload="auto"
                disablePictureInPicture
                controls={false}
                controlsList="nodownload nofullscreen noplaybackrate"
            >
                <source src={media} type="video/mp4" />
            </video>
        );
    } else if (mediaType === 'image') {
        return <img src={media} alt={alt || ''} />;
    }
    return null;
};

const ArticlePage = () => {
    const { t } = useTranslation();
    const loc = useLocalized();
    const { id } = useParams();
    const navigate = useNavigate();

    const findArticle = (list) =>
        (list || []).find((item) => String(item.id) === String(id)) || null;

    const [article, setArticle] = useState(
        findArticle(JSON.parse(localStorage.getItem('activityData')))
    );
    const [notFound, setNotFound] = useState(false);

    // Always start at the top of the article
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        const cached = findArticle(JSON.parse(localStorage.getItem('activityData')));
        if (cached) {
            setArticle(cached);
        }

        // Refresh from the API (also covers deep links on a fresh browser)
        const fetchArticle = async () => {
            try {
                const response = await fetch(`${config.baseUrl}/activities`);
                const data = await response.json();
                const updatedData = data.map((item) => ({
                    ...item,
                    mediaType: item.image.endsWith('.mp4') ? 'video' : 'image',
                }));

                localStorage.setItem('activityData', JSON.stringify(updatedData));

                const found = findArticle(updatedData);
                if (found) {
                    setArticle(found);
                } else if (!cached) {
                    setNotFound(true);
                }
            } catch (error) {
                console.error('Error fetching article:', error);
                if (!cached) {
                    setNotFound(true);
                }
            }
        };

        fetchArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const renderParagraphs = (text) => {
        if (!text) return null;
        return text.split('\n\n').map((paragraph, index) => (
            <React.Fragment key={index}>
                {paragraph}
                <br/><br/>
            </React.Fragment>
        ));
    };

    return (
        <>
            <main className="articlePage container">
                <button
                    className="backLink"
                    onClick={() => navigate('/', { state: { scrollTo: 'activities' } })}
                >
                    <BsArrowLeft/> {t('article.back')}
                </button>

                {article ? (
                    <article>
                        <div className="articleHead">
                            <div className="articleMetaRow">
                                <span className="metaTag">{t('activities.tag')}</span>
                                <span className="metaNum">
                                    N°{article.id < 10 ? `0${article.id}` : article.id}
                                </span>
                            </div>
                            <h1>{loc(article, 'title')}</h1>
                            <p className="articleIntro">{renderParagraphs(loc(article, 'description'))}</p>
                        </div>

                        <div className="articleMedia">
                            <MediaContent
                                media={article.image}
                                mediaType={article.mediaType}
                                alt={loc(article, 'title')}
                            />
                        </div>
                        {article.copyright && (
                            <p className="credit">
                                <Trans
                                    i18nKey="article.photoCredit"
                                    values={{ name: article.copyright }}
                                    components={{ hl: <span style={{color: "var(--highlight)", fontWeight: 600}}/> }}
                                />
                            </p>
                        )}

                        <div className="articleBody">
                            {renderParagraphs(loc(article, 'content'))}
                        </div>
                    </article>
                ) : (
                    <div className="articleEmpty">
                        {notFound
                            ? t('article.notFound')
                            : t('article.loading')}
                    </div>
                )}
            </main>

            <div id="contact">
                <Footer/>
            </div>
        </>
    );
};

export default ArticlePage;
